import { ManufacturerNameVariant } from '../../../services/api/domains/manufacturers/types';
import { NamesTabGroup } from '../../../components/modals/fields/NamesTab';

/**
 * Case/whitespace-insensitive grouping key. Mirrors
 * products/utils/resolvePreviewSource.ts's own `normalizeName` —
 * re-implemented here rather than imported, per this feature's no-cross-
 * domain-dependency convention.
 */
export function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * Collapses the ranked variant list to one option per distinct normalized
 * name (Sub-task 10.1 AC: "Lists all synonyms grouped by normalized name").
 * A group's position is set by its first-encountered variant, so the
 * output order is a pure function of the backend's own rank-tier ORDER BY
 * (api/sql/manufacturers.js's `getManufacturerNameVariants`) — never of
 * which name happens to be the current preferred one. Callers must compute
 * this ONCE from the initial fetch and never re-derive it from a post-
 * mutation refetch, or the list will visibly reorder every time a
 * manual-override row's rank tier changes its position in the raw array
 * (issue #653, Task 16 QA follow-up — the product modal's own synonym list
 * never refetches mid-edit for exactly this reason: `preferred_product_name`
 * is a plain scalar the client already knows, not something it needs the
 * server to re-rank before it can render "currently selected").
 *
 * Returns the generic `NamesTabGroup` shape the shared
 * `components/modals/fields/NamesTab.tsx` component renders (issue #653,
 * Task 16 QA follow-up: extracted the Names tab UI into that shared
 * component, favoring the product modal's own grouping-by-source
 * convention — each distinct source label becomes one row; manufacturer
 * name variants have no "owners" concept, so `owners` is always null).
 */
export function groupVariantsByName(
  variants: ManufacturerNameVariant[]
): NamesTabGroup[] {
  const groups = new Map<string, NamesTabGroup>();
  const order: string[] = [];

  variants.forEach(variant => {
    const key = normalizeName(variant.name);
    let group = groups.get(key);
    if (!group) {
      group = { key, name: variant.name, sources: [] };
      groups.set(key, group);
      order.push(key);
    }
    if (!group.sources.some(s => s.label === variant.label)) {
      group.sources.push({ label: variant.label, owners: null });
    }
  });

  return order.map(key => groups.get(key) as NamesTabGroup);
}

export interface PreviewSource {
  /** The group currently in effect, or null if a manual override's typed
   * text matches none of the captured variants (a brand-new custom name —
   * matches products' resolvePreviewSource returning `variant: null` for
   * the same case). */
  group: NamesTabGroup | null;
  /** "GLEIF", "SAP AML", "LGM Preferred Name", etc. — for the modal
   * header's "Sourced from" line and as a null-safe fallback name. */
  label: string;
  name: string;
}

const MANUAL_LABEL = 'LGM Preferred Name';

/**
 * Manufacturer-side equivalent of products/utils/resolvePreviewSource.ts.
 * Resolves purely from LOCAL state (whether a manual override is active,
 * and its text) plus the frozen, already-rank-ordered `groupedOptions` —
 * never re-fetches or re-ranks anything itself.
 *
 * @param hasManualOverride - whether an active short_display/Manual row exists
 * @param manualOverrideText - that row's current text (ignored if !hasManualOverride)
 * @param groupedOptions - frozen, rank-ordered groups (see groupVariantsByName)
 * @param fallbackName - the manufacturer's own denormalized name, used only
 *   if groupedOptions is empty (no captured variants at all yet)
 */
export function resolvePreviewSource(
  hasManualOverride: boolean,
  manualOverrideText: string,
  groupedOptions: NamesTabGroup[],
  fallbackName: string
): PreviewSource {
  if (hasManualOverride) {
    const normalized = normalizeName(manualOverrideText);
    const match = groupedOptions.find(g => g.key === normalized) ?? null;
    return {
      group: match,
      label: MANUAL_LABEL,
      name: manualOverrideText,
    };
  }

  // The frozen list's rank-1 slot is the Manual/short_display row itself
  // whenever one was active at the initial fetch — that row always
  // outranks every other source while active (Decision 6). Skip any group
  // that is backed ONLY by that Manual row: falling through to it here
  // would keep showing the just-cleared override forever, since
  // `groupedOptions` is deliberately never re-fetched/re-ranked mid-edit
  // (see this function's own doc comment). A group whose text happens to
  // MATCH a real source too (the Manual override text collided with an
  // existing captured name) still qualifies — it has real backing besides
  // the Manual row, so it's still the correct next-best candidate.
  const topRanked =
    groupedOptions.find(g => g.sources.some(s => s.label !== MANUAL_LABEL)) ??
    null;
  const topRankedLabel = topRanked?.sources.find(
    s => s.label !== MANUAL_LABEL
  )?.label;
  return {
    group: topRanked,
    label: topRankedLabel ?? MANUAL_LABEL,
    name: topRanked?.name ?? fallbackName,
  };
}
